import React, { useState, useEffect } from 'react';
import {
  Button,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  GetApp as InstallIcon,
} from '@mui/icons-material';

const PWAInstallButton = ({ variant = 'icon', size = 'medium', color = 'inherit' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // Check if app is already installed
    const isStandaloneMode = window.navigator.standalone === true || 
                            window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(isStandaloneMode);

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if running on iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS && !isStandaloneMode) {
      setIsInstallable(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('PWA installed successfully');
      }
      
      setDeferredPrompt(null);
      setIsInstallable(false);
    } else {
      // For iOS or when prompt is not available
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        alert('To install this app on your iOS device, tap the Share button and then "Add to Home Screen".');
      } else {
        alert('To install this app, look for the install option in your browser menu or address bar.');
      }
    }
  };

  // Don't show if already installed
  if (isStandalone || !isInstallable) {
    return null;
  }

  if (variant === 'button') {
    return (
      <Button
        onClick={handleInstall}
        startIcon={<InstallIcon />}
        variant="outlined"
        size={size}
        color={color}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 2,
          display: { xs: isMobile ? 'none' : 'flex', md: 'flex' }
        }}
      >
        Install App
      </Button>
    );
  }

  return (
    <Tooltip title="Install App">
      <IconButton
        onClick={handleInstall}
        size={size}
        sx={{
          color: color,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'scale(1.1)',
          },
        }}
      >
        <InstallIcon />
      </IconButton>
    </Tooltip>
  );
};

export default PWAInstallButton;